import React, { useState, useEffect, useContext } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Gavel, Clock, Users, Calendar, MapPin, ArrowRight, 
  AlertTriangle, CheckCircle, ChevronRight, Eye, FileText
} from 'lucide-react';

// Helper to build full image URL
const getImageUrl = (url, API) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  // Remove /api prefix from API URL and append the image path
  const baseUrl = API.replace('/api', '');
  return `${baseUrl}${url}`;
};

export default function VacationPage() {
  const { vacationSlug } = useParams();
  const { API } = useContext(AppContext);
  const [vacation, setVacation] = useState(null);
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    fetchVacation();
  }, [vacationSlug]);

  useEffect(() => {
    if (!vacation) return;

    const updateCountdown = () => {
      const now = new Date();
      let targetDate;
      let label;

      if (vacation.statut === 'annoncee') {
        targetDate = new Date(vacation.date_cloture_inscriptions);
        label = 'Clôture des inscriptions';
      } else if (vacation.statut === 'inscriptions_closes') {
        targetDate = new Date(vacation.date_ouverture_vente);
        label = 'Ouverture des enchères';
      } else if (vacation.statut === 'vente_en_cours') {
        targetDate = new Date(vacation.date_cloture_vente);
        label = 'Fin des enchères';
      } else {
        setCountdown(null);
        return;
      }

      const diff = targetDate - now;
      if (diff <= 0) {
        setCountdown({ label, expired: true });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown({
        label,
        days,
        hours,
        minutes,
        seconds,
        isUrgent: diff < 120000 // Less than 2 minutes
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [vacation]);

  const fetchVacation = async () => {
    try {
      // Trigger automatic status check before fetching data
      try {
        await axios.post(`${API}/encheres/scheduler/check-and-update`);
      } catch (e) {
        // Silent fail - scheduler check is optional
        console.log('Scheduler check skipped:', e.message);
      }
      
      const [vacResponse, lotsResponse] = await Promise.all([
        axios.get(`${API}/encheres/vacations/${vacationSlug}`),
        axios.get(`${API}/encheres/vacations/${vacationSlug}/lots`)
      ]);
      setVacation(vacResponse.data);
      setLots(lotsResponse.data || []);
    } catch (error) {
      console.error('Error fetching vacation:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (statut) => {
    const configs = {
      annoncee: { label: 'Inscriptions ouvertes', color: 'bg-green-500', icon: Users },
      inscriptions_closes: { label: 'Inscriptions closes', color: 'bg-yellow-500', icon: Clock },
      vente_en_cours: { label: 'Enchères en cours', color: 'bg-[#F0A22E]', icon: Gavel },
      close: { label: 'Enchères closes', color: 'bg-gray-500', icon: Clock },
      attribuee: { label: 'Vente terminée', color: 'bg-blue-500', icon: CheckCircle }
    };
    const config = configs[statut] || { label: statut, color: 'bg-gray-400', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1 text-sm px-3 py-1`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </Badge>
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#F0A22E]"></div>
      </div>
    );
  }

  if (!vacation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Gavel className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h1 className="text-2xl font-bold text-gray-700 mb-2">Vente non trouvée</h1>
          <Link to="/encheres">
            <Button>Retour aux enchères</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Schema.org ItemList
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": vacation.titre_fr,
    "description": vacation.seo_description || vacation.description_fr,
    "numberOfItems": lots.length,
    "itemListElement": lots.map((lot, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": lot.titre_fr,
        "description": lot.description_vendeur_fr?.slice(0, 200),
        "image": lot.photos?.[0]?.url,
        "url": `https://geots.consulting/encheres/${vacationSlug}/${lot.slug}`
      }
    }))
  };

  // Schema.org Event for the auction
  const eventSchema = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": vacation.titre_fr,
    "description": vacation.seo_description || vacation.description_fr || "Vente par courtage aux enchères de matériel géotechnique",
    "startDate": vacation.date_ouverture_vente,
    "endDate": vacation.date_cloture_vente,
    "eventStatus": vacation.statut === 'close' || vacation.statut === 'attribuee' 
      ? "https://schema.org/EventScheduled" 
      : "https://schema.org/EventScheduled",
    "eventAttendanceMode": "https://schema.org/OnlineEventAttendanceMode",
    "location": {
      "@type": "VirtualLocation",
      "url": `https://geots.consulting/encheres/${vacationSlug}`
    },
    "organizer": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "url": "https://geots.consulting"
    },
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR",
      "availability": vacation.statut === 'annoncee' 
        ? "https://schema.org/InStock" 
        : "https://schema.org/SoldOut",
      "validFrom": vacation.date_publication,
      "url": `https://geots.consulting/encheres/${vacationSlug}`
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://geots.consulting/" },
      { "@type": "ListItem", "position": 2, "name": "Enchères", "item": "https://geots.consulting/encheres" },
      { "@type": "ListItem", "position": 3, "name": vacation.titre_fr, "item": `https://geots.consulting/encheres/${vacationSlug}` }
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{vacation.seo_titre || `${vacation.titre_fr} — vente du ${formatDate(vacation.date_ouverture_vente)}`} | GEOTS</title>
        <meta 
          name="description" 
          content={vacation.seo_description || `${lots.length} lots de matériel géotechnique. ${vacation.description_fr || ''}`} 
        />
        <link rel="canonical" href={`https://geots.consulting/encheres/${vacationSlug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={vacation.seo_titre || vacation.titre_fr} />
        <meta property="og:description" content={vacation.seo_description || `${lots.length} lots de matériel géotechnique mis en vente par courtage aux enchères.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://geots.consulting/encheres/${vacationSlug}`} />
        <meta property="og:site_name" content="GEOTS Consulting" />
        {lots[0]?.photos?.[0]?.url && <meta property="og:image" content={getImageUrl(lots[0].photos[0].url, API)} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={vacation.seo_titre || vacation.titre_fr} />
        <meta name="twitter:description" content={vacation.seo_description || `${lots.length} lots de matériel géotechnique.`} />
        {lots[0]?.photos?.[0]?.url && <meta name="twitter:image" content={getImageUrl(lots[0].photos[0].url, API)} />}
        
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(eventSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
        <div className="container mx-auto px-4 py-12">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
            <Link to="/" className="hover:text-white">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/encheres" className="hover:text-white">Enchères</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white font-medium truncate max-w-[200px]">{vacation.titre_fr}</span>
          </nav>

          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
            <div className="flex-1">
              {getStatusBadge(vacation.statut)}
              
              <h1 className="text-3xl md:text-4xl font-bold mt-4 mb-4">
                {vacation.titre_fr}
              </h1>

              {vacation.description_fr && (
                <p className="text-lg text-gray-300 mb-6 max-w-2xl">
                  {vacation.description_fr}
                </p>
              )}

              {/* Key Dates */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-gray-300 mb-1">
                    <Users className="w-4 h-4" />
                    Clôture des inscriptions
                  </div>
                  <p className="font-semibold">{formatDate(vacation.date_cloture_inscriptions)}</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-sm text-gray-300 mb-1">
                    <Gavel className="w-4 h-4" />
                    Fin des enchères
                  </div>
                  <p className="font-semibold">{formatDate(vacation.date_cloture_vente)}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <span className="text-gray-400">Lots</span>
                  <p className="text-2xl font-bold">{lots.length}</p>
                </div>
                <div>
                  <span className="text-gray-400">Commission incluse</span>
                  <p className="text-2xl font-bold">{vacation.commission_pct}%</p>
                </div>
                <div>
                  <span className="text-gray-400">Frais de dossier</span>
                  <p className="text-2xl font-bold">{vacation.frais_dossier_eur} €</p>
                </div>
              </div>
            </div>

            {/* Countdown */}
            {countdown && !countdown.expired && (
              <div className={`rounded-xl p-6 text-center min-w-[280px] ${
                countdown.isUrgent ? 'bg-[#F0A22E]' : 'bg-white/10'
              }`}>
                <p className="text-sm mb-2 opacity-80">{countdown.label}</p>
                {countdown.days > 0 ? (
                  <p className="text-4xl font-bold">
                    {countdown.days} jour{countdown.days > 1 ? 's' : ''}
                  </p>
                ) : (
                  <div className="flex justify-center gap-2">
                    <div className="text-center">
                      <p className="text-3xl font-bold">{String(countdown.hours).padStart(2, '0')}</p>
                      <p className="text-xs opacity-60">h</p>
                    </div>
                    <span className="text-3xl font-bold">:</span>
                    <div className="text-center">
                      <p className="text-3xl font-bold">{String(countdown.minutes).padStart(2, '0')}</p>
                      <p className="text-xs opacity-60">min</p>
                    </div>
                    <span className="text-3xl font-bold">:</span>
                    <div className="text-center">
                      <p className="text-3xl font-bold">{String(countdown.seconds).padStart(2, '0')}</p>
                      <p className="text-xs opacity-60">sec</p>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* CTA Button - Always visible when inscriptions open */}
            {vacation.statut === 'annoncee' && (
              <Link to={`/encheres/${vacationSlug}/inscription`} className="block">
                <Button className="w-full bg-white text-[#0A2540] hover:bg-gray-100 py-6 text-lg">
                  <Users className="w-5 h-5 mr-2" />
                  S&apos;inscrire pour participer
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Legal Notice */}
      <div className="bg-gray-100 border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-start gap-3 text-sm text-gray-600">
            <AlertTriangle className="w-5 h-5 text-[#F0A22E] flex-shrink-0 mt-0.5" />
            <p>
              <strong>Vente par courtage aux enchères — vente de gré à gré.</strong> Il ne s&apos;agit pas d&apos;une 
              vente aux enchères publiques. Le vendeur reste libre du choix de l&apos;acquéreur et peut refuser 
              l&apos;ensemble des offres.
            </p>
          </div>
        </div>
      </div>

      {/* Lots List */}
      <div className="container mx-auto px-4 py-12">
        <h2 className="text-2xl font-bold text-[#0A2540] mb-6">
          {lots.length} lot{lots.length > 1 ? 's' : ''} proposé{lots.length > 1 ? 's' : ''} à la vente
        </h2>

        {lots.length === 0 ? (
          <Card className="bg-gray-50 border-dashed">
            <CardContent className="py-12 text-center">
              <Gavel className="w-12 h-12 mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">Les lots seront publiés prochainement.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {lots.map((lot, index) => (
              <Link key={lot.id} to={`/encheres/${vacationSlug}/${lot.slug}`}>
                <Card className="hover:shadow-lg transition-shadow group overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="md:w-64 h-48 md:h-auto flex-shrink-0 relative overflow-hidden bg-gray-100">
                        {lot.photos?.[0] ? (
                          <img 
                            src={getImageUrl(lot.photos[0].url, API)} 
                            alt={lot.photos[0].alt_fr || lot.titre_fr}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Gavel className="w-12 h-12 text-gray-300" />
                          </div>
                        )}
                        <div className="absolute top-2 left-2">
                          <Badge className="bg-[#0A2540] text-white">
                            Lot {lot.numero_lot}
                          </Badge>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                          <div className="flex-1">
                            <h3 className="text-xl font-bold text-[#0A2540] mb-2 group-hover:text-[#F0A22E] transition-colors">
                              {lot.titre_fr}
                            </h3>
                            
                            {lot.commune && (
                              <p className="text-sm text-gray-500 flex items-center gap-1 mb-2">
                                <MapPin className="w-4 h-4" />
                                {lot.commune}{lot.departement ? ` (${lot.departement})` : ''}
                              </p>
                            )}

                            <p className="text-gray-600 line-clamp-2 mb-4">
                              {lot.description_vendeur_fr}
                            </p>

                            {/* Badges */}
                            <div className="flex flex-wrap gap-2">
                              {lot.mode_visite === 'obligatoire' && (
                                <Badge variant="outline" className="text-orange-600 border-orange-300">
                                  <Eye className="w-3 h-3 mr-1" />
                                  Visite obligatoire
                                </Badge>
                              )}
                              {lot.reserve_aux_pros && (
                                <Badge variant="outline" className="text-blue-600 border-blue-300">
                                  Réservé aux professionnels
                                </Badge>
                              )}
                              {lot.documents?.length > 0 && (
                                <Badge variant="outline" className="text-gray-600">
                                  <FileText className="w-3 h-3 mr-1" />
                                  {lot.documents.length} document{lot.documents.length > 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Price Block */}
                          <div className="md:text-right md:min-w-[180px]">
                            <p className="text-sm text-gray-500 mb-1">Mise à prix</p>
                            <p className="text-3xl font-bold text-[#0A2540]">
                              {formatPrice(lot.mise_a_prix)} €
                            </p>
                            <p className="text-xs text-gray-400 mb-2">Commission incluse</p>
                            
                            {lot.meilleure_offre && lot.statut === 'ouvert' && (
                              <div className="mt-2 p-2 bg-[#F0A22E]/10 rounded">
                                <p className="text-sm text-gray-600">Meilleure offre</p>
                                <p className="text-xl font-bold text-[#F0A22E]">
                                  {formatPrice(lot.meilleure_offre)} €
                                </p>
                                <p className="text-xs text-gray-500">{lot.nb_offres} offre{lot.nb_offres > 1 ? 's' : ''}</p>
                              </div>
                            )}

                            {lot.statut === 'attribue' && (
                              <Badge className="bg-green-500 text-white mt-2">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Vendu
                              </Badge>
                            )}
                          </div>
                        </div>

                        <div className="mt-4 pt-4 border-t flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Pas d&apos;enchère : {formatPrice(lot.pas_enchere)} €
                          </span>
                          <span className="text-sm text-[#F0A22E] font-medium group-hover:underline flex items-center gap-1">
                            Voir le lot
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* SEO Content */}
      {vacation.seo_texte_fr && (
        <div className="bg-white border-t py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto prose prose-lg">
              <div dangerouslySetInnerHTML={{ __html: vacation.seo_texte_fr }} />
            </div>
          </div>
        </div>
      )}

      {/* Conditions Link */}
      <div className="bg-gray-100 py-8">
        <div className="container mx-auto px-4 text-center">
          <Link to="/encheres/conditions" className="text-gray-600 hover:text-[#0A2540] text-sm underline">
            Consulter les conditions générales de vente
          </Link>
        </div>
      </div>
    </div>
  );
}
